/**
 * Safe helper to open a print preview window without using document.write().
 *
 * document.write() flags in most static-analysis / XSS audits because it
 * blindly executes any string as HTML (including <script> tags). We build a
 * Blob URL instead so the browser parses the HTML as if it were a real
 * document but no `document.write` API is invoked.
 *
 * Callers still must ensure any user-supplied data included in `html` is
 * escaped or sanitized (use DOMPurify for HTML fragments coming from the DB).
 *
 * @param {string} html  Full HTML document string (should include <!DOCTYPE>).
 * @param {{ autoPrint?: boolean, printDelayMs?: number }} [opts]
 */
export function openPrintWindow(html, opts = {}) {
  const { autoPrint = true, printDelayMs = 400 } = opts;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    // Popup blocker
    URL.revokeObjectURL(url);
    return null;
  }
  if (autoPrint) {
    // Wait for the new tab to parse/paint before triggering print
    setTimeout(() => {
      try { w.focus(); w.print(); } catch { /* pop-up may be closed */ }
      // Free the blob once the print dialog is (likely) open
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }, printDelayMs);
  } else {
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  return w;
}
